<template>
  <div class="personal-home">
    <div class="personal-info card-container">
      <div class="card-title">{{ $t(`${i18nKeyPrefix}.label.information`) }}</div>
      <div class="card-content info-list">
        <div
          v-for="(item, index) in informationList"
          class="list-item"
          :key="index"
        >
          <div class="list-item-label">
            <label>{{ $t(item.labelI18nKey) }}</label>
          </div>
          <div class="list-item-content">
            <span>{{ item.value }}</span>
          </div>
        </div>
      </div>
    </div>
    <div
      v-for="(item, index) in homeMenuList"
      class="card-container menu-item-wrapper"
      :key="index"
    >
      <div class="card-content menu-item" @click="handleMenuClick(item)">
        <div class="menu-item-icon">
          <i class="iconfont" :class="item.iconClass"></i>
        </div>
        <div class="menu-item-content">
          <div class="menu-item-name">{{ $t(item.nameI18nKey) }}</div>
          <div class="menu-item-desc">{{ $t(item.descI18nKey) }}</div>
        </div>
        <div class="menu-item-btn" :class="`${item.btn.type}-btn`">
          <i v-if="item.btn.type === MENU_BTN_TYPE.arrow" class="el-icon-arrow-right"></i>
          <el-switch v-if="item.btn.type === MENU_BTN_TYPE.switch"></el-switch>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import _ from 'lodash'
import { mapGetters } from 'vuex'
import { deviceType } from '@/utils/common'
import referralsRoute from '../referralsRoute'
import applicationsRoute from '../applicationsRoute'
import resumeRoute from '../resumeRoute'
import resetPasswordRoute from '../resetPasswordRoute'
import consentRoute from '../consentRoute'

const MENU_BTN_TYPE = {
  arrow: 'arrow',
  switch: 'switch'
}

export default {
  props: {
    personalInfo: {
      required: true
    },
    companyHasReferralPremium: {
      type: Boolean,
      default: true
    },
    companyHasSoftUserPremium: {
      type: Boolean,
      default: true
    }
  },
  data () {
    return {
      MENU_BTN_TYPE,
      i18nKeyPrefix: 'personal.home'
    }
  },
  computed: {
    ...mapGetters('account', ['wxUserHasBindRewards']),
    isPc () {
      return deviceType.isPc
    },
    canApplyJobAsEmployee () {
      const employee = this.personalInfo.employee
      if (employee) {
        return employee.can_apply_job
      }
      return true
    },
    homeMenuList () {
      let routes = [referralsRoute, consentRoute, resetPasswordRoute, {
        meta: {
          mobileHomeMenu: {
            iconClass: 'icona-logout',
            nameI18nKey: 'personal.menu.logout',
            descI18nKey: 'personal.menu.tooltip.logout',
            redirectPath: '/logout/'
          }
        }
      }]
      if (this.canApplyJobAsEmployee) {
        routes = _.concat([applicationsRoute, resumeRoute], routes)
      }
      const menuList = _.map(routes, (item, index) => {
        const menuInfo = item.meta.mobileHomeMenu
        let descI18nKey = menuInfo.descI18nKey
        if (item.name === referralsRoute.name && !this.companyHasReferralPremium) {
          descI18nKey = descI18nKey.replace(/.desc$/, '.noReferralPremiumDesc')
        }
        return {
          route: item,
          iconClass: menuInfo.iconClass,
          nameI18nKey: menuInfo.nameI18nKey,
          descI18nKey: descI18nKey,
          btn: {
            type: MENU_BTN_TYPE.arrow
          }
        }
      })
      return menuList
    },
    informationList () {
      const infoList = [
        {
          labelI18nKey: `${this.i18nKeyPrefix}.label.name`,
          value: this.personalInfo.name
        },
        {
          labelI18nKey: `${this.i18nKeyPrefix}.label.email`,
          value: this.formatEmail(this.personalInfo.email_address)
        },
        {
          labelI18nKey: `${this.i18nKeyPrefix}.label.mobile`,
          value: this.formatMobile(this.personalInfo.mobile)
        },
        {
          labelI18nKey: `${this.i18nKeyPrefix}.label.id`,
          value: this.personalInfo.id
        }
      ]
      return infoList
    },
    isWeChatScan () {
      return this.$route.query.company_id && this.$route.query.is_wechat_scan === '1'
    },
    WxBindSuccess () {
      return this.$route.query.bind_success === '1'
    }
  },
  created () {
    if (this.isPc) {
      this.$router.push({ path: '/recruiting/personal/home', query: this.$route.query })
    }
  },
  mounted () {
    if (this.isWeChatScan && this.wxUserHasBindRewards) {
      if (this.WxBindSuccess) {
        this.$message({
          type: 'success',
          message: this.$t(`${this.i18nKeyPrefix}.message.weChatBindSuccess`)
        })
      } else {
        this.$message({
          type: 'warning',
          message: this.$t(`${this.i18nKeyPrefix}.message.weChatBindFailed`)
        })
      }
    }
  },
  methods: {
    handleMenuClick (menuItem) {
      const routeMeta = menuItem.route.meta
      if (routeMeta.isInclude) {
        this.$router.push({ path: menuItem.route.path, query: this.$route.query })
      } else if ([applicationsRoute, resumeRoute].includes(menuItem.route)) {
        location.href = `${routeMeta.mobileHomeMenu.redirectPath}?company_id=${this.$route.query.company_id || ''}`
      } else {
        location.href = routeMeta.mobileHomeMenu.redirectPath
      }
    },
    formatEmail (val) {
      return val?.replace(/.{1,3}@/, '***@')
    },
    formatMobile (val) {
      return val?.replace(val?.substr(3, 4), '***')
    }
  }
}
</script>

<style lang="scss" scoped>
.personal-home {
  margin-bottom: 40px;
  padding: 0 20px;

  .card-container {
    margin-bottom: 15px;
    padding: 10px 15px;
    border-radius: 5px;
    background: #fff;

    .card-title {
      padding-top: 10px;
      padding-bottom: 10px;
      border-bottom: 1px solid #e2e2e2;
      font-size: 16px;
      font-weight: bold;
      text-align: center;
    }

    .card-content {
      padding-top: 10px;
      padding-bottom: 10px;
    }
  }

  .personal-info {
    .info-list {
      margin-top: 10px;

      .list-item {
        display: flex;
        margin: 5px 0;

        .list-item-label {
          width: 65px;
          color: #aaaaaa;
        }

        .list-item-content {

          flex: 1;
          white-space: nowrap;
          word-break: keep-all;
          text-overflow: ellipsis;
          overflow: hidden;
        }
      }
    }
  }

  .menu-item-wrapper {

    .menu-item {
      display: flex;

      .menu-item-icon {
        display: flex;
        justify-content: center;
        align-items: center;
        overflow: hidden;
        color: #aaaaaa;

        i {
          font-size: 45px;
        }
      }

      .menu-item-content {
        flex: 1;
        margin-left: 20px;
        margin-right: 5px;

        .menu-item-name {
          font-size: 16px;
          font-weight: bold;
        }

        .menu-item-desc {
          overflow: hidden;
          color: #aaaaaa;
        }
      }

      .menu-item-btn {
        display: flex;
        justify-content: center;
        align-items: center;

        &.arrow-btn {
          font-size: 25px;
          color: var(--custom-color);
        }
      }
    }
  }
}
</style>
